import { ClockCircleOutlined, DownOutlined, RightOutlined, WarningFilled } from '@ant-design/icons';
import { Empty, Spin, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import type { Allocation, Project } from '../../DTOS/ResourcePlanning';
import type { Filters } from './planningModel';
import { PersonLabel, WEEKDAYS, type ViewProps } from './PlanningTable';
import {
  allocationProjectLabel,
  cellAction,
  colorOf,
  DISPLAY_DATE,
  hours,
  isWeekend,
  loadColor,
  projectLabel,
  today,
  UNASSIGNED,
  useCanEdit,
} from './shared';

export type GanttGroup = 'team' | 'project';
interface Segment {
  key: string;
  start: number;
  end: number;
  lane: number;
  allocations: Allocation[];
  color: string;
  label: string;
  overloaded: boolean;
}
interface GanttRow {
  key: string;
  label: React.ReactNode;
  segments: Segment[];
  lanes: number;
  allocations: Allocation[];
  target: { teamId: string | null; userId: string; projectId?: string };
}
interface GanttGroupRow {
  key: string;
  label: React.ReactNode;
  track: React.ReactNode;
  rows: GanttRow[];
  allocations: Allocation[];
}

const LABEL = 340,
  TOTAL = 84,
  LANE = 22,
  PAD = 5,
  STRIP = 18;
const sum = (allocations: Allocation[]) => allocations.reduce((total, allocation) => total + allocation.minutes, 0);
const short = (minutes: number) => `${+(minutes / 60).toFixed(1)}`;
const byDate = (allocations: Allocation[]) =>
  allocations.reduce((all, allocation) => all.set(allocation.date, (all.get(allocation.date) || 0) + allocation.minutes), new Map<string, number>());

/** Splits allocations into bars of consecutive working days and stacks overlapping bars in separate lanes. */
const segmentsOf = (
  allocations: Allocation[],
  dates: string[],
  index: Map<string, number>,
  keyOf: (allocation: Allocation) => string,
  describe: (allocations: Allocation[]) => { color: string; label: string },
  overloaded: (allocation: Allocation) => boolean,
) => {
  const groups = new Map<string, Allocation[]>();
  allocations
    .filter((allocation) => index.has(allocation.date))
    .forEach((allocation) => groups.set(keyOf(allocation), [...(groups.get(keyOf(allocation)) || []), allocation]));
  const bars: Allocation[][] = [];
  groups.forEach((list) => {
    let current: Allocation[] = [];
    [...list]
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((allocation) => {
        const previous = current[current.length - 1];
        if (previous && !dates.slice(index.get(previous.date)! + 1, index.get(allocation.date)).every(isWeekend)) {
          bars.push(current);
          current = [];
        }
        current.push(allocation);
      });
    if (current.length) bars.push(current);
  });
  const laneEnds: number[] = [];
  const segments: Segment[] = bars
    .map((bar) => ({ bar, start: index.get(bar[0].date)!, end: index.get(bar[bar.length - 1].date)! }))
    .sort((a, b) => a.start - b.start)
    .map(({ bar, start, end }) => {
      let lane = laneEnds.findIndex((laneEnd) => laneEnd < start);
      if (lane < 0) {
        lane = laneEnds.length;
        laneEnds.push(end);
      } else laneEnds[lane] = end;
      return { key: bar[0].id, start, end, lane, allocations: bar, overloaded: bar.some(overloaded), ...describe(bar) };
    });
  return { segments, lanes: Math.max(1, laneEnds.length) };
};

/**
 * Bars per person and project. With `split`, every bar is divided into days with the hours of that day, each person
 * row gets a strip with the daily total, and team headers show the team's hours per day. The footer and the right
 * column always show totals per day and per row.
 */
const GanttView: React.FC<ViewProps & { group: GanttGroup; split: boolean; filters: Filters; projects: Project[] }> = ({
  model,
  loading,
  group,
  split,
  filters,
  projects,
  onDetails,
  onCreate,
  onDay,
  onSwap,
}) => {
  const [collapsed, setCollapsed] = useState<string[]>([]);
  // Dragging over empty days of a row selects the period of a new allocation.
  const [drag, setDrag] = useState<{ row: string; target: GanttRow['target']; start: number; end: number } | null>(
    null,
  );
  const { dates, rows, loads, visible } = model;
  const canEdit = useCanEdit();
  useEffect(() => {
    if (!drag) return undefined;
    const finish = () => {
      const [start, end] = [Math.min(drag.start, drag.end), Math.max(drag.start, drag.end)];
      setDrag(null);
      // A person outside the team lead's own team opens read-only instead of prefilling a new allocation.
      const creatable = !drag.target.userId || canEdit(drag.target.userId, dates[start]);
      if (start === end || !creatable) onDay({ ...drag.target, date: dates[start] });
      else onCreate({ ...drag.target, date: dates[start], to: dates[end] });
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [drag]);
  const dayWidth = dates.length === 1 ? 640 : dates.length <= 7 ? 120 : dates.length <= 31 ? 38 : 20;
  const width = dayWidth * dates.length;
  const index = new Map(dates.map((date, position) => [date, position]));
  const isOverloaded = (allocation: Allocation) => !!loads.get(`${allocation.userId}:${allocation.date}`)?.overloaded;
  const describeProject = (allocations: Allocation[]) => ({
    color: colorOf(allocations[0].projectId),
    label: `${allocationProjectLabel(allocations[0], model.projectsById.get(allocations[0].projectId))} · ${hours(sum(allocations))}`,
  });
  const dayText = (minutes: number) => (dayWidth >= 90 ? hours(minutes) : dayWidth >= 20 ? short(minutes) : '');

  /** One absolutely positioned cell per day with hours, e.g. the daily totals of a row, a team or the whole plan. */
  const dayCells = (
    totals: Map<string, number>,
    describe: (date: string, minutes: number) => { title: React.ReactNode; background?: string; danger?: boolean },
    style: React.CSSProperties,
  ) =>
    dates.map((date, position) => {
      const minutes = totals.get(date);
      if (!minutes) return null;
      const { title, background, danger } = describe(date, minutes);
      return (
        <Tooltip key={date} title={title}>
          <div
            style={{
              position: 'absolute',
              left: position * dayWidth + 1,
              width: dayWidth - 2,
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 3,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              background,
              color: danger ? '#cf1322' : '#262626',
              ...style,
            }}
          >
            {dayText(minutes)}
          </div>
        </Tooltip>
      );
    });

  const groups: GanttGroupRow[] =
    group === 'team'
      ? rows.map((row) => {
          const totals = byDate(row.allocations);
          return {
            key: row.key,
            allocations: row.allocations,
            label: (
              <span>
                <strong>{row.team.name}</strong>{' '}
                <Tag bordered={false} icon={<ClockCircleOutlined />} style={{ marginLeft: 4 }}>
                  {hours(row.team.dailyMinutes)}
                </Tag>
              </span>
            ),
            track: split
              ? dayCells(
                  totals,
                  (date, total) => ({
                    title: `${dayjs(date).format(DISPLAY_DATE)}: ${hours(total)} / ${hours(row.capacity(date))}`,
                    background: loadColor(total, row.capacity(date)),
                    danger: total > row.capacity(date),
                  }),
                  { top: 6, height: 20, lineHeight: '20px' },
                )
              : dates.map((date, position) => {
                  const total = totals.get(date) || 0,
                    capacity = row.capacity(date);
                  if (!total) return null;
                  const ratio = capacity ? Math.min(1, total / capacity) : 1;
                  return (
                    <Tooltip key={date} title={`${dayjs(date).format(DISPLAY_DATE)}: ${hours(total)} / ${hours(capacity)}`}>
                      <div
                        style={{
                          position: 'absolute',
                          left: position * dayWidth + 1,
                          width: dayWidth - 2,
                          bottom: 3,
                          height: Math.max(3, ratio * 24),
                          borderRadius: 2,
                          background: total > capacity ? '#ff7875' : total === capacity ? '#73d13d' : '#91caff',
                        }}
                      />
                    </Tooltip>
                  );
                }),
            rows: row.people.map((person) => ({
              key: person.key,
              label: <PersonLabel person={person} onSwap={onSwap} />,
              target: { teamId: row.key, userId: person.userId },
              allocations: person.allocations,
              ...segmentsOf(
                person.allocations,
                dates,
                index,
                (allocation) => allocation.projectId,
                describeProject,
                isOverloaded,
              ),
            })),
          };
        })
      : projects
          .filter((project) => {
            const staffed = visible.some((allocation) => allocation.projectId === project.id);
            const unfiltered = !filters.teams.length && !filters.people.length && !filters.overloadedOnly;
            const matches =
              (!filters.projects.length || filters.projects.includes(project.id)) &&
              (!filters.systems.length || project.systems.some((system) => filters.systems.includes(system.id)));
            return (
              staffed ||
              (unfiltered &&
                matches &&
                project.isActive &&
                project.startDate <= dates[dates.length - 1] &&
                project.endDate >= dates[0])
            );
          })
          .map((project) => {
            const allocations = visible.filter((allocation) => allocation.projectId === project.id);
            const start = dates.findIndex((date) => date >= project.startDate),
              end = dates.reduce((last, date, position) => (date <= project.endDate ? position : last), -1);
            const color = colorOf(project.id);
            const people = [
              ...new Map(
                allocations.map((allocation) => [`${allocation.userId}:${allocation.teamId}`, allocation]),
              ).values(),
            ].sort((a, b) => a.userName.localeCompare(b.userName));
            return {
              key: project.id,
              allocations,
              label: (
                <Tooltip
                  title={
                    <>
                      {projectLabel(project)}
                      <br />
                      Responsabil: {project.responsibleName || 'Nedesemnat'}
                    </>
                  }
                >
                  <div style={{ width: '100%', minWidth: 0, lineHeight: 1.4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 10, height: 10, flexShrink: 0, borderRadius: 2, background: color }} />
                      <Typography.Text strong ellipsis>
                        {projectLabel({ number: project.number, name: project.name })}
                      </Typography.Text>
                    </div>
                    <Typography.Text type='secondary' ellipsis style={{ display: 'block', fontSize: 11 }}>
                      {project.systems.map((system) => system.name).join(', ')} ·{' '}
                      {project.responsibleName || 'Nedesemnat'}
                    </Typography.Text>
                  </div>
                </Tooltip>
              ),
              track: (
                <>
                  {project.startDate && start >= 0 && end >= start && (
                    <Tooltip
                      title={`Perioada contract: ${dayjs(project.startDate).format(DISPLAY_DATE)} – ${dayjs(project.endDate).format(DISPLAY_DATE)}`}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: start * dayWidth + 1,
                          width: (end - start + 1) * dayWidth - 2,
                          top: 6,
                          bottom: 6,
                          borderRadius: 4,
                          border: `1px dashed ${color}`,
                          background: `${color}1f`,
                        }}
                      />
                    </Tooltip>
                  )}
                  {split &&
                    dayCells(
                      byDate(allocations),
                      (date, total) => ({ title: `${dayjs(date).format(DISPLAY_DATE)}: ${hours(total)} pe proiect` }),
                      { top: 13, height: 20, lineHeight: '20px' },
                    )}
                </>
              ),
              rows: people.map((first) => {
                const own = allocations.filter(
                  (allocation) => allocation.userId === first.userId && allocation.teamId === first.teamId,
                );
                return {
                  key: `${project.id}:${first.userId}:${first.teamId}`,
                  target: { teamId: first.teamId, userId: first.userId, projectId: project.id },
                  allocations: own,
                  label: (
                    <span>
                      {first.userName}{' '}
                      <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                        · {first.teamName || 'Alocare directa'}
                      </Typography.Text>
                    </span>
                  ),
                  ...segmentsOf(
                    own,
                    dates,
                    index,
                    (allocation) => allocation.teamId || UNASSIGNED,
                    (bar) => ({ color, label: hours(sum(bar)) }),
                    isOverloaded,
                  ),
                };
              }),
            };
          });

  if (!groups.length)
    return loading ? (
      <Spin style={{ display: 'block', margin: 48 }} />
    ) : (
      <Empty description='Nicio alocare pentru filtrele selectate.' />
    );
  const months = dates.reduce<{ label: string; count: number }[]>((all, date) => {
    const label = dayjs(date).format('MMMM YYYY');
    if (all.length && all[all.length - 1].label === label) all[all.length - 1].count++;
    else all.push({ label, count: 1 });
    return all;
  }, []);
  const todayIndex = index.get(today()) ?? -1;
  // Every allocation belongs to exactly one group in both layouts, so the footer sums what the chart shows.
  const shown = groups.flatMap((groupRow) => groupRow.allocations);
  const shownUsers = [...new Set(rows.flatMap((row) => row.people.map((person) => person.userId)))];
  const capacityOn = (date: string) => shownUsers.reduce((total, userId) => total + model.capacityFor(userId, date), 0);
  const labelStyle: React.CSSProperties = {
    width: LABEL,
    minWidth: LABEL,
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: '#fff',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #f0f0f0',
  };
  const totalStyle: React.CSSProperties = {
    width: TOTAL,
    minWidth: TOTAL,
    position: 'sticky',
    right: 0,
    zIndex: 2,
    background: '#fff',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    fontSize: 12,
    borderLeft: '1px solid #f0f0f0',
  };
  const dayAt = (event: React.MouseEvent<HTMLDivElement>) =>
    Math.max(
      0,
      Math.min(
        dates.length - 1,
        Math.floor((event.clientX - event.currentTarget.getBoundingClientRect().left) / dayWidth),
      ),
    );
  const segmentTooltip = (segment: Segment) => (
    <>
      <div>
        <strong>
          {allocationProjectLabel(segment.allocations[0], model.projectsById.get(segment.allocations[0].projectId))}
        </strong>
      </div>
      <div>
        {segment.allocations[0].userName} · {segment.allocations[0].teamName || 'Alocare directa'}
      </div>
      <div>
        {dayjs(segment.allocations[0].date).format(DISPLAY_DATE)} –{' '}
        {dayjs(segment.allocations[segment.allocations.length - 1].date).format(DISPLAY_DATE)}
      </div>
      <div>
        {segment.allocations.length} zile · {hours(sum(segment.allocations))}
      </div>
      {segment.overloaded && (
        <div>
          <WarningFilled /> Depasire de program in perioada
        </div>
      )}
    </>
  );
  const bar = (segment: Segment) => {
    const box: React.CSSProperties = {
      position: 'absolute',
      left: segment.start * dayWidth + 2,
      width: (segment.end - segment.start + 1) * dayWidth - 4,
      top: PAD + segment.lane * LANE,
      height: LANE - 3,
      lineHeight: `${LANE - 3}px`,
      borderRadius: 4,
      fontSize: 11,
      cursor: 'pointer',
    };
    const action = {
      onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
      ...cellAction(`Detalii ${segment.label}`, () => onDetails(segment.allocations)),
    };
    if (!split)
      return (
        <div
          {...action}
          style={{
            ...box,
            background: segment.color,
            color: '#fff',
            padding: '0 6px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            boxShadow: segment.overloaded ? '0 0 0 2px #ff4d4f' : undefined,
          }}
        >
          {segment.overloaded && <WarningFilled style={{ marginRight: 4 }} />}
          {segment.label}
        </div>
      );
    // One block per worked day; weekend gaps inside a bar stay empty.
    return (
      <div {...action} style={box}>
        {segment.allocations.map((allocation) => (
          <div
            key={allocation.id}
            style={{
              position: 'absolute',
              left: (index.get(allocation.date)! - segment.start) * dayWidth,
              width: dayWidth - 4,
              top: 0,
              bottom: 0,
              background: segment.color,
              color: '#fff',
              textAlign: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              borderRadius: 3,
              boxShadow: isOverloaded(allocation) ? 'inset 0 0 0 2px #ff4d4f' : undefined,
            }}
          >
            {dayWidth >= 90 ? `${allocation.projectNumber} · ${hours(allocation.minutes)}` : dayText(allocation.minutes)}
          </div>
        ))}
      </div>
    );
  };
  const toggle = (key: string) =>
    setCollapsed((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  return (
    <Spin spinning={!!loading}>
      <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 }}>
        <div style={{ width: LABEL + width + TOTAL, position: 'relative' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', height: 48 }}>
            <div style={{ ...labelStyle, fontWeight: 600 }}>
              {group === 'team' ? 'Echipa / persoana' : 'Proiect / persoana'}
            </div>
            <div style={{ width }}>
              <div style={{ display: 'flex', height: 24 }}>
                {months.map((month) => (
                  <div
                    key={month.label}
                    style={{
                      width: month.count * dayWidth,
                      borderLeft: '1px solid #f0f0f0',
                      padding: '2px 6px',
                      fontWeight: 600,
                      fontSize: 12,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textTransform: 'capitalize',
                    }}
                  >
                    {month.label}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', height: 24 }}>
                {dates.map((date) => (
                  <div
                    key={date}
                    style={{
                      width: dayWidth,
                      textAlign: 'center',
                      fontSize: 11,
                      lineHeight: '24px',
                      color: isWeekend(date) ? '#bfbfbf' : date === today() ? '#cf1322' : '#595959',
                      fontWeight: date === today() ? 700 : undefined,
                    }}
                  >
                    {dayWidth >= 38 ? `${WEEKDAYS[dayjs(date).day()]} ` : ''}
                    {dayjs(date).format('D')}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ ...totalStyle, fontWeight: 600, fontSize: 14 }}>Total</div>
          </div>
          <div style={{ position: 'absolute', left: LABEL, top: 48, bottom: 0, width, pointerEvents: 'none' }}>
            {dates.map((date, position) => (
              <div
                key={date}
                style={{
                  position: 'absolute',
                  left: position * dayWidth,
                  width: dayWidth,
                  top: 0,
                  bottom: 0,
                  background: isWeekend(date) ? 'rgba(0,0,0,0.035)' : undefined,
                  borderLeft: split && position ? '1px solid #f5f5f5' : undefined,
                }}
              />
            ))}
            {todayIndex >= 0 && (
              <div
                style={{
                  position: 'absolute',
                  left: todayIndex * dayWidth + dayWidth / 2 - 1,
                  width: 2,
                  top: 0,
                  bottom: 0,
                  background: '#ff4d4f',
                  opacity: 0.5,
                }}
              />
            )}
          </div>
          {groups.map((groupRow) => (
            <React.Fragment key={groupRow.key}>
              <div
                style={{
                  display: 'flex',
                  height: group === 'project' ? 46 : 32,
                  background: '#fafafa',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div
                  style={{ ...labelStyle, background: '#fafafa', cursor: 'pointer', gap: 6 }}
                  onClick={() => toggle(groupRow.key)}
                >
                  {collapsed.includes(groupRow.key) ? (
                    <RightOutlined style={{ fontSize: 10 }} />
                  ) : (
                    <DownOutlined style={{ fontSize: 10 }} />
                  )}
                  {groupRow.label}
                </div>
                <div style={{ position: 'relative', width }}>{groupRow.track}</div>
                <div style={{ ...totalStyle, background: '#fafafa', fontWeight: 600 }}>
                  {hours(sum(groupRow.allocations))}
                </div>
              </div>
              {!collapsed.includes(groupRow.key) &&
                groupRow.rows.map((row) => (
                  <div
                    key={row.key}
                    style={{
                      display: 'flex',
                      height: row.lanes * LANE + PAD * 2 + (split ? STRIP : 0),
                      borderBottom: '1px solid #f5f5f5',
                    }}
                  >
                    <div style={labelStyle}>{row.label}</div>
                    <div
                      style={{ position: 'relative', width, cursor: 'copy', userSelect: 'none' }}
                      title='Click pentru planificarea zilei sau trageti peste mai multe zile pentru alocare noua'
                      onMouseDown={(event) => {
                        if (event.button === 0) {
                          const position = dayAt(event);
                          setDrag({ row: row.key, target: row.target, start: position, end: position });
                        }
                      }}
                      onMouseMove={(event) => {
                        if (drag?.row === row.key) {
                          const position = dayAt(event);
                          if (position !== drag.end) setDrag({ ...drag, end: position });
                        }
                      }}
                    >
                      {drag?.row === row.key && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 2,
                            bottom: 2,
                            left: Math.min(drag.start, drag.end) * dayWidth,
                            width: (Math.abs(drag.end - drag.start) + 1) * dayWidth,
                            background: 'rgba(22,119,255,0.15)',
                            border: '1px dashed #1677ff',
                            borderRadius: 4,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                      {row.segments.map((segment) => (
                        <Tooltip key={segment.key} title={segmentTooltip(segment)}>
                          {bar(segment)}
                        </Tooltip>
                      ))}
                      {split &&
                        dayCells(
                          byDate(row.allocations),
                          (date, minutes) => {
                            const load = loads.get(`${row.target.userId}:${date}`);
                            return {
                              title: (
                                <>
                                  {dayjs(date).format(DISPLAY_DATE)}: {hours(minutes)} in acest rand
                                  {load && (
                                    <div>
                                      Total zi, toate proiectele: {hours(load.minutes)} / {hours(load.capacity)}
                                    </div>
                                  )}
                                </>
                              ),
                              background: load ? loadColor(load.minutes, load.capacity) : '#f5f5f5',
                              danger: load?.overloaded,
                            };
                          },
                          { bottom: 3, height: STRIP - 4, lineHeight: `${STRIP - 4}px` },
                        )}
                    </div>
                    <div style={totalStyle}>{hours(sum(row.allocations))}</div>
                  </div>
                ))}
            </React.Fragment>
          ))}
          <div style={{ display: 'flex', height: 30, background: '#f0f5ff', borderTop: '1px solid #d6e4ff' }}>
            <div style={{ ...labelStyle, background: '#f0f5ff', fontWeight: 600 }}>Total pe zi</div>
            <div style={{ position: 'relative', width }}>
              {dayCells(
                byDate(shown),
                (date, total) => {
                  const capacity = capacityOn(date);
                  return {
                    title: `${dayjs(date).format(DISPLAY_DATE)}: ${hours(total)} alocate din ${hours(capacity)} disponibile`,
                    background: loadColor(total, capacity),
                    danger: total > capacity,
                  };
                },
                { top: 5, height: 20, lineHeight: '20px' },
              )}
            </div>
            <div style={{ ...totalStyle, background: '#f0f5ff', fontWeight: 700 }}>{hours(sum(shown))}</div>
          </div>
        </div>
      </div>
    </Spin>
  );
};
export default GanttView;
